import { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../utils/cn';

/**
 * Button component with multiple variants and sizes.
 *
 * @param {Object} props
 * @param {'primary'|'secondary'|'ghost'|'danger'} [props.variant='primary'] - Visual style
 * @param {'sm'|'md'|'lg'} [props.size='md'] - Button size
 * @param {boolean} [props.loading=false] - Show loading spinner
 * @param {boolean} [props.disabled=false] - Disable button
 * @param {React.ReactNode} [props.leftIcon] - Icon before text
 * @param {React.ReactNode} [props.rightIcon] - Icon after text
 * @param {string} [props.className] - Additional classes
 * @param {React.ReactNode} props.children - Button content
 */
export const Button = forwardRef(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled = false,
    leftIcon,
    rightIcon,
    className,
    children,
    ...props
  },
  ref
) {
  const baseStyles = `
    inline-flex items-center justify-center gap-2 font-medium
    rounded-md transition-colors duration-150
    focus:outline-none focus:ring-2 focus:ring-offset-2
    disabled:opacity-50 disabled:cursor-not-allowed
  `;

  const variants = {
    primary: `
      bg-primary-600 text-white
      hover:bg-primary-700
      focus:ring-primary-500
    `,
    secondary: `
      bg-white text-neutral-700 border border-neutral-300
      hover:bg-neutral-50
      focus:ring-primary-500
    `,
    ghost: `
      bg-transparent text-neutral-700
      hover:bg-neutral-100
      focus:ring-primary-500
    `,
    danger: `
      bg-error-600 text-white
      hover:bg-error-700
      focus:ring-error-500
    `,
  };

  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-2.5 text-base',
  };

  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        baseStyles,
        variants[variant],
        sizes[size],
        className
      )}
      {...props}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : leftIcon ? (
        <span className="h-4 w-4">{leftIcon}</span>
      ) : null}
      {children}
      {rightIcon && !loading && (
        <span className="h-4 w-4">{rightIcon}</span>
      )}
    </button>
  );
});
